import { useState, useEffect } from "react";
import axios from "axios";
import { buildAxiosAuthenticationHeaders } from "@src/cloud-connect/api/util/build-axios-authentication-headers";
import { ragUrlEnvVar } from "@src/cloud-connect/feature-toggles/feature-toggles";
import { streamAnswer, SourceDocument } from "./stream-utils";
import {
  StreamTool,
  completeRunningTools,
  mergeToolCall,
  normalizeHistoricalToolCalls,
  normalizeToolCall,
} from "@src/components/molecules/tool-activity-section/stream-tools";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { useI18N } from "@src/language/language-context";

export interface MessageImage {
  thumbnail?: string;
  interpretation: string;
  localFile?: File;
}

export interface Message {
  id: string;
  serverMessageId?: string; // we keep the server message ID separate for easier handling of feedback, since the client-generated ID is needed for rendering and state management during streaming
  role: "user" | "assistant";
  content: string;
  thinkingContent?: string;
  isStreaming?: boolean;
  createdAt?: string;
  useful?: boolean;
  sources?: SourceDocument[];
  tools?: StreamTool[];
  images?: MessageImage[];
}

interface UseAIChatProps {
  userId: string | null;
  namespace: string | null;
}

export const useAIChat = ({ userId, namespace }: UseAIChatProps) => {
  const i18n = useI18N();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [expandedThinking, setExpandedThinking] = useState<Set<string>>(
    new Set(),
  );
  const [expandedSources, setExpandedSources] = useState<Set<string>>(
    new Set(),
  );
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const texts = {
    errorFetchingChatText: i18n("modules.aiRagChat.errorFetchingChatText"),
    errorStreamAnswer: i18n("modules.aiRagChat.errorStreamAnswer"),
  };

  const addFiles = (files: File[]) => {
    setSelectedFiles(files);
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleExpanded = (
    messageId: string,
    setter: React.Dispatch<React.SetStateAction<Set<string>>>,
  ) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) next.delete(messageId);
      else next.add(messageId);
      return next;
    });
  };

  const toggleThinkingExpanded = (messageId: string) => {
    toggleExpanded(messageId, setExpandedThinking);
  };

  const toggleSourcesExpanded = (messageId: string) => {
    toggleExpanded(messageId, setExpandedSources);
  };

  const updateMessageFeedback = (messageId: string, useful: boolean) => {
    setMessages((prev) =>
      prev.map((msg) => (msg.id === messageId ? { ...msg, useful } : msg)),
    );
  };

  const getChatMessages = async (sessionIdToFetch: string) => {
    if (!namespace) {
      errorSnackbar(texts.errorFetchingChatText);
      return;
    }

    try {
      const headers = buildAxiosAuthenticationHeaders();
      const response = await axios.get(
        `${ragUrlEnvVar()}/api/v1/ns/${namespace}/sessions/${sessionIdToFetch}`,
        { headers },
      );

      if (response.data && Array.isArray(response.data.messages)) {
        const fetchedMessages: Message[] = response.data.messages.map(
          (msg: any) => {
            const rawContent = msg.content || "";
            const thinkMatch = rawContent.match(/<think>([\s\S]*?)<\/think>/);
            const thinking = thinkMatch ? thinkMatch[1].trim() : "";
            const messageId = msg.id || crypto.randomUUID();

            return {
              id: messageId,
              serverMessageId: messageId,
              role: msg.user_id === null ? "assistant" : "user",
              content: rawContent,
              thinkingContent: thinking,
              isStreaming: false,
              useful: msg.useful,
              createdAt: msg.created_at,
              sources: msg.sources || undefined,
              tools: msg.tool_calls
                ? normalizeHistoricalToolCalls(msg.tool_calls)
                : undefined,
              images: Array.isArray(msg.images)
                ? msg.images.map((img: any) => ({
                    thumbnail: img.thumbnail,
                    interpretation: img.interpretation,
                  }))
                : undefined,
            };
          },
        );

        setMessages(fetchedMessages);
      }
    } catch (error: any) {
      if (error?.response?.status === 404) {
        localStorage.removeItem("userAiChatSession");
        setSessionId(null);
        setMessages([]);
        return;
      }

      console.error("Error fetching chat history:", error);
      errorSnackbar(texts.errorFetchingChatText);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;
    if (!namespace) {
      errorSnackbar(texts.errorFetchingChatText);
      return;
    }

    const question = input.trim();
    const attachedFiles = selectedFiles;
    let activeSessionId = sessionId;

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: "user",
      content: question,
      images:
        attachedFiles.length > 0
          ? attachedFiles.map((file) => ({
              interpretation: file.name,
              localFile: file,
            }))
          : undefined,
    };

    const assistantId = crypto.randomUUID();
    const assistantMessage: Message = {
      id: assistantId,
      role: "assistant",
      content: "",
      isStreaming: true,
    };

    setMessages((prev) => [...prev, userMessage, assistantMessage]);
    setInput("");
    setSelectedFiles([]);
    setIsLoading(true);

    try {
      for await (const event of streamAnswer(
        namespace,
        question,
        sessionId ?? undefined,
        attachedFiles.length > 0 ? attachedFiles : undefined,
      )) {
        if (event.type === "meta") {
          if ((event as any).sessionId) {
            activeSessionId = (event as any).sessionId as string;
            if (!sessionId) {
              const sid = activeSessionId;
              setSessionId(sid);
              localStorage.setItem(
                "userAiChatSession",
                JSON.stringify({
                  sessionId: sid,
                  userId,
                }),
              );
            }
          }

          if ((event as any).assistantMessageId) {
            const serverMsgId = (event as any).assistantMessageId as string;
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantId
                  ? { ...msg, serverMessageId: serverMsgId }
                  : msg,
              ),
            );
          }

          if ((event as any).sources) {
            const sources = (event as any).sources as SourceDocument[];
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantId ? { ...msg, sources } : msg,
              ),
            );
          }
        } else if (event.type === "token") {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantId
                ? {
                    ...msg,
                    content: msg.content + event.data,
                    // A token while a tool is still running means that tool just finished.
                    tools: msg.tools
                      ? completeRunningTools(msg.tools)
                      : msg.tools,
                  }
                : msg,
            ),
          );

          // auto-expand thinking when streaming starts
          if (event.data.includes("<think>")) {
            setExpandedThinking((prev) => {
              if (prev.has(assistantId)) return prev;
              const next = new Set(prev);
              next.add(assistantId);
              return next;
            });
          }

          // auto-collapse thinking when complete
          if (event.data.includes("</think>")) {
            setExpandedThinking((prev) => {
              if (!prev.has(assistantId)) return prev;
              const next = new Set(prev);
              next.delete(assistantId);
              return next;
            });
          }
        } else if (event.type === "tool-call") {
          const parsedTools = normalizeToolCall(event.data);
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantId
                ? {
                    ...msg,
                    tools: parsedTools.reduce(mergeToolCall, msg.tools ?? []),
                  }
                : msg,
            ),
          );
        } else if (event.type === "error") {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantId
                ? {
                    ...msg,
                    content: `${event.data}`,
                    isStreaming: false,
                    tools: msg.tools
                      ? completeRunningTools(msg.tools)
                      : msg.tools,
                  }
                : msg,
            ),
          );
          break;
        } else if (event.type === "done") {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantId && msg.tools
                ? { ...msg, tools: completeRunningTools(msg.tools) }
                : msg,
            ),
          );
          break;
        }
      }
    } catch (err) {
      console.error("streaming error", err);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantId
            ? {
                ...msg,
                content: texts.errorStreamAnswer,
                isStreaming: false,
                tools: msg.tools ? completeRunningTools(msg.tools) : msg.tools,
              }
            : msg,
        ),
      );
    } finally {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantId
            ? {
                ...msg,
                content: msg.content
                  .replace(/\r\n/g, "\n")
                  .replace(/\n{3,}/g, "\n\n")
                  .trimStart(),
                isStreaming: false,
              }
            : msg,
        ),
      );
      setIsLoading(false);

      // Backend thumbnails are only available via the session history — refetch
      // so the attached images render under the user message.
      if (attachedFiles.length > 0 && activeSessionId) {
        void getChatMessages(activeSessionId);
      }
    }
  };

  const resetConversation = () => {
    setMessages([]);
    setInput("");
    setIsLoading(false);
    setSessionId(null);
    setSelectedFiles([]);
    localStorage.removeItem("userAiChatSession");
  };

  useEffect(() => {
    if (!namespace) {
      return;
    }

    try {
      const storedSessionRaw = localStorage.getItem("userAiChatSession");
      if (storedSessionRaw) {
        const storedSession = JSON.parse(storedSessionRaw);

        if (
          storedSession &&
          storedSession.userId === userId &&
          storedSession.sessionId
        ) {
          setSessionId(storedSession.sessionId);
          getChatMessages(storedSession.sessionId);
        }
      }
    } catch (error) {
      console.error("Error restoring session:", error);
    }
  }, [namespace, userId]);

  return {
    messages,
    input,
    setInput,
    isLoading,
    sessionId,
    expandedThinking,
    expandedSources,
    sendMessage,
    resetConversation,
    toggleThinkingExpanded,
    toggleSourcesExpanded,
    updateMessageFeedback,
    selectedFiles,
    addFiles,
    removeFile,
  };
};
